/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.5.0 - CalendarView (OPTIMIZED)
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Button, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faCalendarDay,
  faPlus,
  faEye,
  faEdit,
  faClock,
  faUsers,
  faSync,
  faFilter,
  faSearch,
  faTimes,
  faCalendarWeek,
  faCalendarCheck
} from '@fortawesome/free-solid-svg-icons';
import {
  faWhatsapp,
  faInstagram,
  faFacebookF,
  faTelegram
} from '@fortawesome/free-brands-svg-icons';
import { ScheduledMessage } from '../../../types/scheduler.types';
import { useScheduler } from '../../../hooks/useScheduler';
import { getMonthName, getDayNameShort } from '../../../types/dateUtils';

interface CalendarViewProps {
  onEventClick?: (schedule: ScheduledMessage) => void;
  onDateClick?: (date: Date) => void;
}

interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  schedules: ScheduledMessage[];
}

type ViewMode = 'month' | 'week' | 'day';

const CalendarView: React.FC<CalendarViewProps> = ({ onEventClick, onDateClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSchedules, setSelectedSchedules] = useState<ScheduledMessage[]>([]);
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduledMessage | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterChannel, setFilterChannel] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  const { schedules, isLoading, refreshSchedules } = useScheduler();

  useEffect(() => {
    const interval = setInterval(() => {
      refreshSchedules();
    }, 30000);
    return () => clearInterval(interval);
  }, [refreshSchedules]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePreviousMonth();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextMonth();
      } else if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        handleToday();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentDate]);

  const filteredSchedules = useMemo(() => {
    let filtered = schedules;

    if (filterChannel) {
      filtered = filtered.filter(s => s.channel === filterChannel);
    }

    if (filterStatus) {
      filtered = filtered.filter(s => s.status === filterStatus);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.message.text.toLowerCase().includes(query) ||
        s.channel.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [schedules, filterChannel, filterStatus, searchQuery]);

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay();
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    
    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date,
        dayNumber: prevMonthLastDay - i,
        isCurrentMonth: false,
        isToday: false,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        schedules: []
      });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      const isToday = date.getTime() === today.getTime();
      
      const daySchedules = filteredSchedules.filter(schedule => {
        const scheduleDate = new Date(schedule.schedule.scheduledAt);
        scheduleDate.setHours(0, 0, 0, 0);
        return scheduleDate.getTime() === date.getTime();
      });
      
      days.push({
        date,
        dayNumber: day,
        isCurrentMonth: true,
        isToday,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        schedules: daySchedules
      });
    }
    
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        dayNumber: day,
        isCurrentMonth: false,
        isToday: false,
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        schedules: []
      });
    }
    
    return days;
  }, [currentDate, filteredSchedules]);

  const totalEvents = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return filteredSchedules.filter(s => {
      const d = new Date(s.schedule.scheduledAt);
      return d.getFullYear() === year && d.getMonth() === month;
    }).length;
  }, [filteredSchedules, currentDate]);

  const getChannelIcon = (channel: string) => {
    const icons: Record<string, any> = {
      whatsapp: faWhatsapp,
      instagram: faInstagram,
      facebook: faFacebookF,
      telegram: faTelegram
    };
    return icons[channel.toLowerCase()] || faWhatsapp;
  };

  const getChannelColor = (channel: string) => {
    const colors: Record<string, string> = {
      whatsapp: '#25D366',
      instagram: '#E4405F',
      facebook: '#1877F2',
      telegram: '#0088CC'
    };
    return colors[channel.toLowerCase()] || '#2ca799';
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDayClick = (day: CalendarDay) => {
    if (!day.isCurrentMonth) return;
    setSelectedDate(day.date);
    setSelectedSchedules(day.schedules);
    setShowDayModal(true);
  };

  const handleScheduleClick = (schedule: ScheduledMessage, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedSchedule(schedule);
    setShowDetailModal(true);
  };

  const handleCreateEvent = () => {
    if (onDateClick) {
      onDateClick(new Date());
    }
  };

  const clearFilters = () => {
    setFilterChannel(null);
    setFilterStatus(null);
    setSearchQuery('');
  };

  return (
    <div style={{ padding: '16px', backgroundColor: 'var(--bs-body-bg)', borderRadius: '12px' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0" style={{ textTransform: 'capitalize', fontWeight: 600 }}>
            {getMonthName(currentDate.getMonth())} {currentDate.getFullYear()}
          </h5>
          <Badge bg="none" style={{ backgroundColor: '#2ca799', fontSize: '0.75rem' }}>
            {totalEvents} eventos
          </Badge>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          {/* Search */}
          <div style={{ position: 'relative', minWidth: '200px' }}>
            <input
              type="text"
              placeholder="Buscar eventos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 32px 6px 10px',
                borderRadius: '6px',
                border: '1px solid var(--bs-border-color)',
                backgroundColor: 'var(--bs-body-bg)',
                color: 'var(--bs-body-color)',
                fontSize: '0.85rem'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--bs-secondary-color)',
                  padding: '2px'
                }}
              >
                <FontAwesomeIcon icon={faTimes} size="xs" />
              </button>
            )}
          </div>

          {/* Filters */}
          <ButtonGroup size="sm">
            <Button
              variant={filterChannel === 'whatsapp' ? 'success' : 'outline-secondary'}
              onClick={() => setFilterChannel(filterChannel === 'whatsapp' ? null : 'whatsapp')}
              style={{ padding: '4px 8px', fontSize: '0.8rem' }}
            >
              <FontAwesomeIcon icon={faWhatsapp} />
            </Button>
            <Button
              variant={filterChannel === 'instagram' ? 'danger' : 'outline-secondary'}
              onClick={() => setFilterChannel(filterChannel === 'instagram' ? null : 'instagram')}
              style={{ padding: '4px 8px', fontSize: '0.8rem' }}
            >
              <FontAwesomeIcon icon={faInstagram} />
            </Button>
            <Button
              variant={filterChannel === 'facebook' ? 'primary' : 'outline-secondary'}
              onClick={() => setFilterChannel(filterChannel === 'facebook' ? null : 'facebook')}
              style={{ padding: '4px 8px', fontSize: '0.8rem' }}
            >
              <FontAwesomeIcon icon={faFacebookF} />
            </Button>
            <Button
              variant={filterChannel === 'telegram' ? 'info' : 'outline-secondary'}
              onClick={() => setFilterChannel(filterChannel === 'telegram' ? null : 'telegram')}
              style={{ padding: '4px 8px', fontSize: '0.8rem' }}
            >
              <FontAwesomeIcon icon={faTelegram} />
            </Button>
          </ButtonGroup>

          <ButtonGroup size="sm">
            <Button
              variant={filterStatus === 'pending' ? 'warning' : 'outline-secondary'}
              onClick={() => setFilterStatus(filterStatus === 'pending' ? null : 'pending')}
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            >
              Pendiente
            </Button>
            <Button
              variant={filterStatus === 'sent' ? 'success' : 'outline-secondary'}
              onClick={() => setFilterStatus(filterStatus === 'sent' ? null : 'sent')}
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            >
              Enviado
            </Button>
            <Button
              variant={filterStatus === 'failed' ? 'danger' : 'outline-secondary'}
              onClick={() => setFilterStatus(filterStatus === 'failed' ? null : 'failed')}
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            >
              Fallido
            </Button>
          </ButtonGroup>

          {(filterChannel || filterStatus || searchQuery) && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={clearFilters}
              style={{ padding: '4px 10px', fontSize: '0.8rem' }}
            >
              <FontAwesomeIcon icon={faTimes} className="me-1" />
              Limpiar
            </Button>
          )}

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleToday}
            style={{ padding: '4px 10px', fontSize: '0.8rem', fontWeight: 600 }}
          >
            Hoy
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={refreshSchedules}
            disabled={isLoading}
            style={{ padding: '4px 10px' }}
          >
            <FontAwesomeIcon icon={faSync} spin={isLoading} />
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={handleCreateEvent}
            style={{ backgroundColor: '#2ca799', border: 'none', padding: '4px 12px', fontSize: '0.8rem' }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Crear
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handlePreviousMonth}
          style={{ borderRadius: '6px', padding: '4px 12px' }}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleNextMonth}
          style={{ borderRadius: '6px', padding: '4px 12px' }}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '4px' }}>
        {[0, 1, 2, 3, 4, 5, 6].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: '600', color: 'var(--bs-secondary-color)', padding: '4px 0' }}>
            {getDayNameShort(d)}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', minHeight: '500px' }}>
        {calendarDays.map((day, idx) => (
          <div
            key={idx}
            onClick={() => handleDayClick(day)}
            style={{
              padding: '6px',
              borderRadius: '6px',
              border: '1px solid var(--bs-border-color)',
              backgroundColor: day.isToday ? 'rgba(44, 167, 153, 0.05)' : day.isCurrentMonth ? 'var(--bs-body-bg)' : 'var(--bs-secondary-bg)',
              cursor: day.isCurrentMonth ? 'pointer' : 'default',
              opacity: day.isCurrentMonth ? 1 : 0.5,
              minHeight: '90px',
              maxHeight: '90px',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.2s ease',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (day.isCurrentMonth) {
                e.currentTarget.style.backgroundColor = day.isToday ? 'rgba(44, 167, 153, 0.1)' : 'var(--bs-secondary-bg)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = day.isToday ? 'rgba(44, 167, 153, 0.05)' : day.isCurrentMonth ? 'var(--bs-body-bg)' : 'var(--bs-secondary-bg)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ fontSize: '0.8rem', fontWeight: day.isToday ? '700' : '500', color: day.isToday ? '#2ca799' : day.isWeekend ? 'var(--bs-danger)' : 'var(--bs-body-color)', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{day.dayNumber}</span>
              {day.isToday && <Badge bg="none" style={{ backgroundColor: '#2ca799', fontSize: '0.6rem', padding: '1px 4px' }}>HOY</Badge>}
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
              {day.schedules.slice(0, 2).map(s => {
                const time = new Date(s.schedule.scheduledAt).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
                return (
                  <div
                    key={s.id}
                    onClick={(e) => handleScheduleClick(s, e)}
                    style={{
                      fontSize: '0.65rem',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      backgroundColor: `${getChannelColor(s.channel)}20`,
                      borderLeft: `2px solid ${getChannelColor(s.channel)}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${getChannelColor(s.channel)}40`;
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${getChannelColor(s.channel)}20`;
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <FontAwesomeIcon icon={getChannelIcon(s.channel)} style={{ color: getChannelColor(s.channel), fontSize: '0.6rem' }} />
                    <span>{time}</span>
                  </div>
                );
              })}
              {day.schedules.length > 2 && (
                <div style={{ fontSize: '0.65rem', padding: '2px 4px', textAlign: 'center', color: 'var(--bs-secondary-color)', fontWeight: 600 }}>
                  +{day.schedules.length - 2}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Day Modal - WindowManager Style */}
      {selectedDate && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: showDayModal ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
            padding: '20px'
          }}
          onClick={() => setShowDayModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bs-body-bg)',
              borderRadius: '8px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--bs-border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 600, textTransform: 'capitalize' }}>
                <FontAwesomeIcon icon={faCalendarDay} className="me-2" style={{ color: '#2ca799' }} />
                {selectedDate.toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <button
                onClick={() => setShowDayModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--bs-secondary-color)',
                  padding: '4px',
                  fontSize: '1rem'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '16px', overflowY: 'auto', flex: 1 }}>
              {selectedSchedules.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--bs-secondary-color)' }}>
                  <FontAwesomeIcon icon={faCalendarDay} size="2x" className="mb-2" />
                  <div>No hay eventos programados</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedSchedules.map(s => {
                    const time = new Date(s.schedule.scheduledAt).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
                    return (
                      <div
                        key={s.id}
                        onClick={() => { setShowDayModal(false); setSelectedSchedule(s); setShowDetailModal(true); }}
                        style={{
                          padding: '10px',
                          borderRadius: '6px',
                          border: '1px solid var(--bs-border-color)',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          backgroundColor: 'var(--bs-body-bg)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bs-secondary-bg)';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bs-body-bg)';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <FontAwesomeIcon icon={getChannelIcon(s.channel)} style={{ color: getChannelColor(s.channel) }} />
                          <Badge bg="none" style={{ backgroundColor: getChannelColor(s.channel), color: '#fff', fontSize: '0.7rem', padding: '2px 6px' }}>
                            {s.channel.toUpperCase()}
                          </Badge>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            <FontAwesomeIcon icon={faClock} className="me-1" />
                            {time}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.85rem', marginBottom: '6px', color: 'var(--bs-body-color)' }}>
                          {s.message.text.substring(0, 100)}{s.message.text.length > 100 ? '...' : ''}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
                          <FontAwesomeIcon icon={faUsers} className="me-1" />
                          {s.recipients.contactIds.length} destinatarios
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid var(--bs-border-color)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}>
              <button
                onClick={() => setShowDayModal(false)}
                style={{
                  padding: '6px 16px',
                  borderRadius: '6px',
                  border: '1px solid var(--bs-border-color)',
                  backgroundColor: 'transparent',
                  color: 'var(--bs-body-color)',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Cerrar
              </button>
              <button
                onClick={() => { if (onDateClick) onDateClick(selectedDate); setShowDayModal(false); }}
                style={{
                  padding: '6px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#2ca799',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                Crear Evento
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Detail Modal - WindowManager Style */}
      {selectedSchedule && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: showDetailModal ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
            padding: '20px'
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bs-body-bg)',
              borderRadius: '8px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--bs-border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>
                <FontAwesomeIcon icon={getChannelIcon(selectedSchedule.channel)} className="me-2" style={{ color: getChannelColor(selectedSchedule.channel) }} />
                Detalle del Evento
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--bs-secondary-color)',
                  padding: '4px',
                  fontSize: '1rem'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '16px', overflowY: 'auto', flex: 1 }}>
              <div className="mb-3">
                <Badge bg="none" style={{ backgroundColor: getChannelColor(selectedSchedule.channel), color: '#fff', fontSize: '0.8rem', padding: '6px 10px' }}>
                  <FontAwesomeIcon icon={getChannelIcon(selectedSchedule.channel)} className="me-2" />
                  {selectedSchedule.channel.toUpperCase()}
                </Badge>
              </div>
              
              <div className="mb-3">
                <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', marginBottom: '4px', fontWeight: 600 }}>MENSAJE:</div>
                <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{selectedSchedule.message.text}</div>
              </div>
              
              <div className="mb-3">
                <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', marginBottom: '4px', fontWeight: 600 }}>FECHA Y HORA:</div>
                <div style={{ fontSize: '0.9rem' }}>
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  {new Date(selectedSchedule.schedule.scheduledAt).toLocaleString('es-CL')}
                </div>
              </div>
              
              <div className="mb-3">
                <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', marginBottom: '4px', fontWeight: 600 }}>DESTINATARIOS:</div>
                <div style={{ fontSize: '0.9rem' }}>
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  {selectedSchedule.recipients.contactIds.length} contactos
                </div>
              </div>
              
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', marginBottom: '4px', fontWeight: 600 }}>ESTADO:</div>
                <Badge bg={selectedSchedule.status === 'sent' ? 'success' : selectedSchedule.status === 'failed' ? 'danger' : 'warning'} style={{ fontSize: '0.8rem', padding: '4px 10px' }}>
                  {selectedSchedule.status === 'sent' ? 'Enviado' : selectedSchedule.status === 'failed' ? 'Fallido' : 'Pendiente'}
                </Badge>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid var(--bs-border-color)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '6px 16px',
                  borderRadius: '6px',
                  border: '1px solid var(--bs-border-color)',
                  backgroundColor: 'transparent',
                  color: 'var(--bs-body-color)',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Cerrar
              </button>
              <button
                onClick={() => { if (onEventClick) onEventClick(selectedSchedule); setShowDetailModal(false); }}
                style={{
                  padding: '6px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#2ca799',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}
              >
                <FontAwesomeIcon icon={faEdit} className="me-1" />
                Editar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CalendarView;